

import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { 
  selectCurrentUser, 
  selectCurrentOrganization,
  selectUserName,
  selectUserEmail,
  selectUserRole,
  selectApiMessage,
  selectAuthError,
  clearApiMessage,
  clearError
} from '../../store/slices/authSlice';
import {
  updateProfile,
  changePassword,
  toggleTwoFactor,
  logoutUser,
  clearAuthState,
  updateNotificationPreferences,
  getNotificationPreferences,
  toggleNotificationPreference,
  resetNotificationPreferences
} from '../../store/slices/authSlice';
import {
  Mail,
  Smartphone,
  Bell,
  Cake,
  AlertTriangle,
  BarChart3,
  User,
  XCircle,
  Inbox,
  Key,
  Lock,
  Unlock,
  CheckCircle2,
  Loader2,
  Wifi,
  CreditCard,
  RefreshCw,
  UserPlus,
  Lightbulb,
  Pencil,
  Save,
  Trash2,
  Circle,
  Church,
  Eye,
} from 'lucide-react';

const Settings = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
  
  // Get user from Redux store using selectors
  const currentUser = useSelector(selectCurrentUser);
  const organization = useSelector(selectCurrentOrganization);
  const userName = useSelector(selectUserName);
  const userEmail = useSelector(selectUserEmail);
  const userRole = useSelector(selectUserRole);
  const apiMessage = useSelector(selectApiMessage);
  const authError = useSelector(selectAuthError);

  // Password change refs
  const currentPasswordRef = useRef(null);
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  // Debug log
  console.log('🔍 Settings - Current user from Redux:', currentUser);
  console.log('🔍 Settings - Organization:', organization);
  console.log('🔍 Settings - User Name:', userName);
  console.log('🔍 Settings - User Email:', userEmail);
  console.log('🔍 Settings - User Role:', userRole);

  // Track if component is mounted (to prevent showing messages on initial load)
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Display API messages - ONLY show when an action is taken (not on initial load)
  useEffect(() => {
    if (apiMessage && showToast && !isInitialLoad) {
      // Skip "User profile retrieved successfully" and other silent messages
      const silentMessages = [
        'User profile retrieved successfully',
        'User profile retrieved',
        'Profile retrieved successfully'
      ];
      
      const shouldIgnore = silentMessages.some(silent => 
        apiMessage.toLowerCase().includes(silent.toLowerCase())
      );
      
      if (!shouldIgnore) {
        showToast(apiMessage, authError ? 'error' : 'success');
      }
      
      // Always clear the message
      setTimeout(() => {
        dispatch(clearApiMessage());
        dispatch(clearError());
      }, 5000);
    }
  }, [apiMessage, authError, dispatch, showToast, isInitialLoad]);

  // Set initial load to false after component mounts
  useEffect(() => {
    setIsInitialLoad(false);
  }, []);

  // Initialize church profile with data from Redux
  const [churchProfile, setChurchProfile] = useState({
    name: organization || currentUser?.organization || 'Grace Chapel International',
    pastor: userName || currentUser?.name || 'Rev. James Mensah',
    phone: currentUser?.phone || '+233244000001',
    email: userEmail || currentUser?.email || 'admin@gracechapel.org',
    location: 'Accra, Ghana',
    website: 'www.gracechapel.org'
  });

  const [apiSettings, setApiSettings] = useState({
    provider: "Africa's Talking",
    apiKey: 'at_live_xxxxxxxxxxxx',
    senderId: 'GRACECHAPEL'
  });

  // Notification preferences from API - Using correct types that match backend
  const [notifications, setNotifications] = useState([
    { id: 'email', label: 'Email Notifications', icon: Mail, desc: 'Receive notifications via email', on: true },
    { id: 'sms', label: 'SMS Notifications', icon: Smartphone, desc: 'Receive notifications via SMS', on: true },
    { id: 'push', label: 'Push Notifications', icon: Bell, desc: 'Receive browser push notifications', on: true },
    { id: 'birthday', label: 'Birthday Reminders', icon: Cake, desc: 'Get reminded about member birthdays', on: true },
    { id: 'low_credit', label: 'Low Credit Alerts', icon: AlertTriangle, desc: 'Alert when SMS credits are low', on: true },
    { id: 'weekly_digest', label: 'Weekly Digest', icon: BarChart3, desc: 'Receive weekly summary reports', on: false },
    { id: 'new_member', label: 'New Member Alerts', icon: User, desc: 'Get notified when new members join', on: true },
    { id: 'failed_sms', label: 'Failed SMS Alerts', icon: XCircle, desc: 'Alert when SMS delivery fails', on: false },
    { id: 'new_inbox', label: 'New Inbox Messages', icon: Inbox, desc: 'Notify when new messages arrive', on: true }
  ]);

  // Load notification preferences from API
  useEffect(() => {
    const loadPreferences = async () => {
      if (currentUser) {
        try {
          setIsLoadingPreferences(true);
          const result = await dispatch(getNotificationPreferences()).unwrap();
          if (result.success && result.preferences) {
            // Update notifications with API data
            setNotifications(prev => prev.map(n => ({
              ...n,
              on: result.preferences[n.id] !== undefined ? result.preferences[n.id] : n.on
            })));
          }
        } catch (error) {
          console.error('Failed to load notification preferences:', error);
        } finally {
          setIsLoadingPreferences(false);
        }
      }
    };
    
    loadPreferences();
  }, [dispatch, currentUser]);

  const [teamMembers, setTeamMembers] = useState([
    { 
      id: 1, 
      name: userName || currentUser?.name || 'Rev. James Mensah', 
      role: userRole || currentUser?.role || 'Super Admin', 
      email: userEmail || currentUser?.email || 'james@gracechapel.org', 
      color: '#4f46e5' 
    },
    { id: 2, name: 'Sis. Patricia Adu', role: 'Admin', email: 'patricia@gracechapel.org', color: '#059669' },
    { id: 3, name: 'Bro. Felix Owusu', role: 'Staff', email: 'felix@gracechapel.org', color: '#d97706' }
  ]);

  // Update profile when user data changes
  useEffect(() => {
    if (currentUser) {
      setChurchProfile(prev => ({
        ...prev,
        name: organization || currentUser.organization || prev.name,
        pastor: userName || currentUser.name || prev.pastor,
        phone: currentUser.phone || prev.phone,
        email: userEmail || currentUser.email || prev.email
      }));
      
      // Update first team member with current user data
      setTeamMembers(prev => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[0] = {
            ...updated[0],
            name: userName || currentUser.name || updated[0].name,
            role: userRole || currentUser.role || updated[0].role,
            email: userEmail || currentUser.email || updated[0].email
          };
        }
        return updated;
      });
    }
  }, [currentUser, organization, userName, userEmail, userRole]);

  // Refs for team member form
  const teamNameRef = useRef(null);
  const teamEmailRef = useRef(null);
  const teamRoleRef = useRef(null);

  // Refs for edit team member
  const editTeamNameRef = useRef(null);
  const editTeamEmailRef = useRef(null);
  const editTeamRoleRef = useRef(null);
  const [editingMemberId, setEditingMemberId] = useState(null);

  // ==================== TOAST HELPER ====================

  const showToastMessage = (message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  };

  // ==================== PROFILE ACTIONS ====================

  // Handle Profile Save - Uses Redux updateProfile
  const handleProfileSave = () => {
    if (!churchProfile.name.trim()) {
      showToastMessage('Please enter a church name', 'error');
      return;
    }
    if (!churchProfile.pastor.trim()) {
      showToastMessage('Please enter a pastor name', 'error');
      return;
    }
    if (!churchProfile.phone.trim()) {
      showToastMessage('Please enter a phone number', 'error');
      return;
    }

    // Clear previous messages
    dispatch(clearApiMessage());
    dispatch(clearError());

    setIsSubmitting(true);

    // Split name into first and last name
    const nameParts = churchProfile.pastor.split(' ');
    const firstName = nameParts[0] || churchProfile.pastor;
    const lastName = nameParts.slice(1).join(' ') || '';

    // Dispatch updateProfile action
    dispatch(updateProfile({
      first_name: firstName,
      last_name: lastName,
      phone: churchProfile.phone,
      bio: currentUser?.bio || '',
      department: currentUser?.department || '',
      position: currentUser?.position || ''
    }))
    .unwrap()
    .then(() => {
      setIsSubmitting(false);
    })
    .catch(() => {
      setIsSubmitting(false);
    });
  };

  // ==================== CHANGE PASSWORD ====================

  const handleChangePassword = () => {
    // Clear previous messages
    dispatch(clearApiMessage());
    dispatch(clearError());

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Key size={16} style={{ verticalAlign: '-3px' }} /> Change Password</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input 
              className="form-input" 
              type="password" 
              ref={currentPasswordRef}
              placeholder="Enter your current password" 
            />
          </div>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input 
              className="form-input" 
              type="password" 
              ref={newPasswordRef}
              placeholder="Enter new password" 
            />
            <div className="form-hint">Minimum 8 characters with at least one number and special character</div>
          </div>
          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input 
              className="form-input" 
              type="password" 
              ref={confirmPasswordRef}
              placeholder="Confirm new password" 
            />
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--blue-bg)', 
            borderRadius: '8px', 
            fontSize: '12px', 
            color: 'var(--blue)' 
          }}>
<Lock size={14} style={{ verticalAlign: '-2px' }} /> Your password will be securely encrypted.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm"
            disabled={isSubmitting}
            onClick={async () => {
              const current = currentPasswordRef.current?.value || '';
              const newPass = newPasswordRef.current?.value || '';
              const confirm = confirmPasswordRef.current?.value || '';

              if (!current) {
                showToastMessage('Please enter your current password', 'error');
                return;
              }
              if (newPass.length < 8) {
                showToastMessage('Password must be at least 8 characters', 'error');
                return;
              }
              if (newPass !== confirm) {
                showToastMessage('Passwords do not match', 'error');
                return;
              }

              setIsSubmitting(true);

              dispatch(changePassword({
                current_password: current,
                new_password: newPass,
                new_password_confirmation: confirm
              }))
              .unwrap()
              .then(() => {
                setIsSubmitting(false);
                closeModal();
              })
              .catch(() => {
                setIsSubmitting(false);
              });
            }}
          >
            {isSubmitting ? <><Loader2 size={16} style={{ verticalAlign: '-3px' }} /> Changing...</> : <><Key size={16} style={{ verticalAlign: '-3px' }} /> Change Password</>}
          </button>
        </div>
      </div>
    );
  };

  // ==================== TWO-FACTOR AUTH ====================

  const handleToggle2FA = () => {
    // Clear previous messages
    dispatch(clearApiMessage());
    dispatch(clearError());

    const isEnabled = currentUser?.two_factor_enabled || false;

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title">{isEnabled ? <Lock size={16} style={{ verticalAlign: '-3px' }} /> : <Unlock size={16} style={{ verticalAlign: '-3px' }} />} Two-Factor Authentication</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ 
            textAlign: 'center',
            padding: '20px 0'
          }}>
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>
              {isEnabled ? <Lock size={48} strokeWidth={1.5} /> : <Unlock size={48} strokeWidth={1.5} />}
            </div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--navy)' }}>
              {isEnabled ? 'Disable Two-Factor Authentication?' : 'Enable Two-Factor Authentication?'}
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-mid)', marginTop: '8px' }}>
              {isEnabled 
                ? 'This will remove an extra layer of security from your account.'
                : 'This will add an extra layer of security to your account.'}
            </p>
            <div style={{ 
              marginTop: '16px',
              padding: '12px',
              background: isEnabled ? 'var(--red-bg)' : 'var(--green-bg)',
              borderRadius: '8px',
              fontSize: '13px',
              color: isEnabled ? 'var(--red)' : 'var(--green)'
            }}>
              {isEnabled
                ? <><AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> Your account will be less secure without 2FA.</>
                : <><CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> You will need to verify your identity using an authenticator app.</>}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Phone Number (for SMS verification)</label>
            <input 
              className="form-input" 
              defaultValue={currentUser?.phone || '+233244000001'}
              placeholder="+233244123456"
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className={`btn ${isEnabled ? 'btn-danger' : 'btn-gold'} btn-sm`}
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              dispatch(toggleTwoFactor())
              .unwrap()
              .then(() => {
                setIsSubmitting(false);
                closeModal();
              })
              .catch(() => {
                setIsSubmitting(false);
              });
            }}
          >
            {isSubmitting ? <><Loader2 size={16} style={{ verticalAlign: '-3px' }} /> Toggling...</> : (isEnabled ? 'Disable 2FA' : 'Enable 2FA')}
          </button>
        </div>
      </div>
    );
  };

  // ==================== API SETTINGS ====================

  const handleApiSave = () => {
    if (!apiSettings.apiKey.trim()) {
      showToastMessage('Please enter an API key', 'error');
      return;
    }
    if (!apiSettings.senderId.trim()) {
      showToastMessage('Please enter a sender ID', 'error');
      return;
    }
    showToastMessage('✅ API settings saved!', 'success');
  };

  // ==================== TEST CONNECTION ====================

  const handleTestConnection = () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Wifi size={16} style={{ verticalAlign: '-3px' }} /> Testing Connection</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center', color: 'var(--green)' }}><CheckCircle2 size={48} strokeWidth={1.5} /></div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--green)' }}>
              Connection Successful!
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginTop: '8px' }}>
              Connected to <strong>{apiSettings.provider}</strong> with sender ID <strong>{apiSettings.senderId}</strong>
            </div>
            <div style={{ 
              marginTop: '12px',
              padding: '12px',
              background: 'var(--cream)',
              borderRadius: '8px',
              fontSize: '12px',
              color: 'var(--text-light)',
              textAlign: 'left'
            }}>
              <div><BarChart3 size={13} style={{ verticalAlign: '-2px' }} /> API Status: <span style={{ color: 'var(--green)', fontWeight: '600' }}>Active</span></div>
              <div><Smartphone size={13} style={{ verticalAlign: '-2px' }} /> Sender ID: {apiSettings.senderId}</div>
              <div><CreditCard size={13} style={{ verticalAlign: '-2px' }} /> Credits: 5,234 remaining</div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // ==================== NOTIFICATIONS ====================

  const toggleNotification = async (id) => {
    // Find the notification
    const notification = notifications.find(n => n.id === id);
    if (!notification) return;

    // Optimistically update UI
    setNotifications(prev => prev.map(n => 
      n.id === id ? { ...n, on: !n.on } : n
    ));

    try {
      const result = await dispatch(toggleNotificationPreference(id)).unwrap();
      if (result.success) {
        // Update with actual value from API
        setNotifications(prev => prev.map(n => 
          n.id === id ? { ...n, on: result.preference?.enabled ?? !n.on } : n
        ));
        // showToastMessage(
        //   `🔔 ${notification.label}: ${result.preference?.enabled ? 'Enabled' : 'Disabled'}`,
        //   'info'
        // );
      }
    } catch (error) {
      // Revert on error
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, on: notification.on } : n
      ));
      showToastMessage(`Failed to update ${notification.label}`, 'error');
    }
  };

  const handleSaveNotifications = async () => {
    // Clear previous messages
    dispatch(clearApiMessage());
    dispatch(clearError());

    setIsSubmitting(true);

    // Prepare data for API - use the type as key
    const preferencesData = {};
    notifications.forEach(n => {
      // The key should be the type (email, sms, push, etc.)
      // not the database field name (email_notifications, sms_notifications, etc.)
      preferencesData[n.id] = n.on;
    });

    try {
      const result = await dispatch(updateNotificationPreferences(preferencesData)).unwrap();
      if (result.success) {
        // Update UI with API response
        if (result.preferences) {
          setNotifications(prev => prev.map(n => ({
            ...n,
            on: result.preferences[n.id] !== undefined ? result.preferences[n.id] : n.on
          })));
        }
        // Message will be shown via apiMessage useEffect
      }
    } catch (error) {
      // Error will be shown via apiMessage useEffect
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetNotifications = async () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><RefreshCw size={16} style={{ verticalAlign: '-3px' }} /> Reset Notification Preferences</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to reset all notification preferences to defaults?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--amber-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--amber)' 
          }}>
<AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> This will reset all notification settings to their default values.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm"
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(resetNotificationPreferences()).unwrap();
                if (result.success && result.preferences) {
                  setNotifications(prev => prev.map(n => ({
                    ...n,
                    on: result.preferences[n.id] !== undefined ? result.preferences[n.id] : n.on
                  })));
                }
                closeModal();
              } catch (error) {
                // Error shown via apiMessage
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Reset to Defaults
          </button>
        </div>
      </div>
    );
  };

  // ==================== TEAM MEMBERS ====================

  const handleInviteTeamMember = () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><UserPlus size={16} style={{ verticalAlign: '-3px' }} /> Invite Team Member</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input 
              className="form-input" 
              ref={teamNameRef}
              placeholder="e.g. John Doe"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Email Address *</label>
            <input 
              className="form-input" 
              ref={teamEmailRef}
              type="email"
              placeholder="e.g. john@church.org"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Role</label>
            <select 
              className="form-select" 
              ref={teamRoleRef}
              defaultValue="Staff"
            >
              <option value="Super Admin">Super Admin</option>
              <option value="Admin">Admin</option>
              <option value="Staff">Staff</option>
              <option value="Viewer">Viewer</option>
            </select>
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--blue-bg)', 
            borderRadius: '8px', 
            fontSize: '12px', 
            color: 'var(--blue)' 
          }}>
<Lightbulb size={14} style={{ verticalAlign: '-2px' }} /> An invitation email will be sent to the user with access instructions.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              const name = teamNameRef.current?.value?.trim() || '';
              const email = teamEmailRef.current?.value?.trim() || '';
              const role = teamRoleRef.current?.value || 'Staff';

              if (!name) {
                showToastMessage('Please enter a name', 'error');
                return;
              }
              if (!email) {
                showToastMessage('Please enter an email', 'error');
                return;
              }
              if (!email.includes('@')) {
                showToastMessage('Please enter a valid email', 'error');
                return;
              }

              const colors = ['#4f46e5', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2'];
              const newMember = {
                id: teamMembers.length + 1,
                name: name,
                email: email,
                role: role,
                color: colors[teamMembers.length % colors.length]
              };

              setTeamMembers([...teamMembers, newMember]);
              showToastMessage(`✅ ${name} invited as ${role}!`, 'success');
              closeModal();
            }}
          >
            Send Invite
          </button>
        </div>
      </div>
    );
  };

  const handleEditTeamMember = (member) => {
    setEditingMemberId(member.id);

    setTimeout(() => {
      if (editTeamNameRef.current) editTeamNameRef.current.value = member.name;
      if (editTeamEmailRef.current) editTeamEmailRef.current.value = member.email;
      if (editTeamRoleRef.current) editTeamRoleRef.current.value = member.role;
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Team Member</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input 
              className="form-input" 
              ref={editTeamNameRef}
              defaultValue=""
            />
          </div>
          <div className="form-group">
            <label className="form-label">Email Address *</label>
            <input 
              className="form-input" 
              ref={editTeamEmailRef}
              type="email"
              defaultValue=""
            />
          </div>
          <div className="form-group">
            <label className="form-label">Role</label>
            <select 
              className="form-select" 
              ref={editTeamRoleRef}
              defaultValue=""
            >
              <option value="Super Admin">Super Admin</option>
              <option value="Admin">Admin</option>
              <option value="Staff">Staff</option>
              <option value="Viewer">Viewer</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              const name = editTeamNameRef.current?.value?.trim() || '';
              const email = editTeamEmailRef.current?.value?.trim() || '';
              const role = editTeamRoleRef.current?.value || 'Staff';

              if (!name) {
                showToastMessage('Please enter a name', 'error');
                return;
              }
              if (!email) {
                showToastMessage('Please enter an email', 'error');
                return;
              }
              if (!email.includes('@')) {
                showToastMessage('Please enter a valid email', 'error');
                return;
              }

              setTeamMembers(teamMembers.map(m => 
                m.id === member.id 
                  ? { ...m, name, email, role }
                  : m
              ));
              showToastMessage(`✅ ${name} updated!`, 'success');
              setEditingMemberId(null);
              closeModal();
            }}
          >
<Save size={16} style={{ verticalAlign: '-3px' }} /> Save Changes
          </button>
        </div>
      </div>
    );
  };

  const handleRemoveTeamMember = (member) => {
    const superAdmins = teamMembers.filter(m => m.role === 'Super Admin');
    if (member.role === 'Super Admin' && superAdmins.length === 1) {
      showToastMessage('⚠️ Cannot remove the only Super Admin', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Remove Team Member</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to remove <strong>{member.name}</strong> from the team?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
<AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> This will revoke all access for {member.name}.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            onClick={() => {
              setTeamMembers(teamMembers.filter(m => m.id !== member.id));
              showToastMessage(`🗑️ ${member.name} removed`, 'error');
              closeModal();
            }}
          >
            Remove Member
          </button>
        </div>
      </div>
    );
  };

  const handleViewTeamMember = (member) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><User size={16} style={{ verticalAlign: '-3px' }} /> Team Member Details</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px',
            padding: '16px',
            background: 'var(--cream)',
            borderRadius: '10px',
            marginBottom: '16px'
          }}>
            <div style={{ 
              width: '56px', 
              height: '56px', 
              borderRadius: '50%', 
              background: member.color, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              fontSize: '20px', 
              fontWeight: '700', 
              color: 'white',
              flexShrink: 0
            }}>
              {member.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--navy)' }}>
                {member.name}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
                {member.role}
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Email</span>
              <span>{member.email}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Role</span>
              <span>
                <span className={`badge ${member.role === 'Super Admin' ? 'badge-navy' : member.role === 'Admin' ? 'badge-blue' : 'badge-amber'}`}>
                  {member.role}
                </span>
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Status</span>
              <span style={{ color: 'var(--green)' }}><Circle size={10} fill="currentColor" style={{ verticalAlign: '-1px' }} /> Active</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Member Since</span>
              <span>January 2024</span>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button className="btn btn-gold btn-sm" onClick={() => {
            closeModal();
            setTimeout(() => handleEditTeamMember(member), 100);
          }}>
<Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Member
          </button>
        </div>
      </div>
    );
  };

  // ==================== DANGER ZONE ====================

  const handleDeleteAllData = () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete All Member Data</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ 
            padding: '16px', 
            background: 'var(--red-bg)', 
            borderRadius: '10px',
            textAlign: 'center',
            marginBottom: '16px'
          }}>
            <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center', color: 'var(--red)' }}><AlertTriangle size={48} strokeWidth={1.5} /></div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--red)' }}>
              This action cannot be undone!
            </div>
          </div>
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            You are about to permanently delete all member records, messages, and campaign history.
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '13px',
            color: 'var(--text-mid)'
          }}>
            <strong>Data that will be deleted:</strong>
            <ul style={{ marginTop: '8px', paddingLeft: '20px', lineHeight: '1.8' }}>
              <li>All member profiles ({Math.floor(Math.random() * 1000) + 500} members)</li>
              <li>All message logs ({Math.floor(Math.random() * 5000) + 1000} messages)</li>
              <li>All campaign data</li>
              <li>All poll responses</li>
              <li>All attendance records</li>
            </ul>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            onClick={() => {
              showToastMessage('Please contact support to delete data', 'info');
              closeModal();
            }}
          >
            Delete All Data
          </button>
        </div>
      </div>
    );
  };

  // ==================== GET ROLE BADGE CLASS ====================

  const getRoleBadgeClass = (role) => {
    switch(role) {
      case 'Super Admin': return 'badge-navy';
      case 'Admin': return 'badge-blue';
      case 'Staff': return 'badge-amber';
      default: return 'badge-amber';
    }
  };

  return (
    <div className="grid-2">
      <div>
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-title" style={{ marginBottom: '16px' }}>Church Profile</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px', padding: '14px', background: 'var(--cream)', borderRadius: '10px' }}>
            <div style={{ width: '56px', height: '56px', background: 'var(--navy)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold-light)' }}><Church size={28} /></div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--navy)' }}>{churchProfile.name}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>Member since January 2024</div>
            </div>
          </div>
          {[
            ['Church Name', churchProfile.name, (v) => setChurchProfile({ ...churchProfile, name: v })],
            ['Pastor / Leader Name', churchProfile.pastor, (v) => setChurchProfile({ ...churchProfile, pastor: v })],
            ['Phone Number', churchProfile.phone, (v) => setChurchProfile({ ...churchProfile, phone: v })],
            ['Email', churchProfile.email, (v) => setChurchProfile({ ...churchProfile, email: v })],
            ['Location', churchProfile.location, (v) => setChurchProfile({ ...churchProfile, location: v })],
            ['Website', churchProfile.website, (v) => setChurchProfile({ ...churchProfile, website: v })]
          ].map(([l, v, onChange]) => (
            <div key={l} className="form-group">
              <label className="form-label">{l}</label>
              <input className="form-input" value={v} onChange={(e) => onChange(e.target.value)} />
            </div>
          ))}
          <button
            className="btn btn-gold btn-sm"
            onClick={handleProfileSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? <><Loader2 size={16} style={{ verticalAlign: '-3px' }} /> Saving...</> : <><Save size={16} style={{ verticalAlign: '-3px' }} /> Save Profile</>}
          </button>
        </div>

        <div className="card">
          <div className="card-title" style={{ marginBottom: '16px' }}>Security Settings</div>
          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 12px',
              background: 'var(--cream)',
              borderRadius: '8px'
            }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>
                  Change Password
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                  Update your account password
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={handleChangePassword}>
                <Key size={14} style={{ verticalAlign: '-2px' }} /> Change
              </button>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 12px',
              background: 'var(--cream)',
              borderRadius: '8px'
            }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>
                  Two-Factor Authentication
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                  {currentUser?.two_factor_enabled ? <><CheckCircle2 size={12} style={{ verticalAlign: '-2px' }} /> Enabled</> : <><XCircle size={12} style={{ verticalAlign: '-2px' }} /> Disabled</>}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={handleToggle2FA}>
                {currentUser?.two_factor_enabled ? 'Disable' : 'Enable'}
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-title" style={{ marginBottom: '16px' }}>SMS Provider Settings</div>
          <div className="form-group">
            <label className="form-label">SMS Provider</label>
            <select className="form-select" value={apiSettings.provider} onChange={(e) => setApiSettings({ ...apiSettings, provider: e.target.value })}>
              <option>Africa's Talking</option>
              <option>Arkesel</option>
              <option>Hubtel</option>
              <option>Twilio</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">API Key</label>
            <input className="form-input" type="password" value={apiSettings.apiKey} onChange={(e) => setApiSettings({ ...apiSettings, apiKey: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Default Sender ID</label>
            <input className="form-input" value={apiSettings.senderId} onChange={(e) => setApiSettings({ ...apiSettings, senderId: e.target.value })} />
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button className="btn btn-ghost btn-sm" onClick={handleTestConnection}>
              <Wifi size={14} style={{ verticalAlign: '-2px' }} /> Test Connection
            </button>
            <button className="btn btn-gold btn-sm" onClick={handleApiSave}>
              <Save size={14} style={{ verticalAlign: '-2px' }} /> Save
            </button>
          </div>
        </div>
      </div>

      <div>
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header">
            <div className="card-title" style={{ marginBottom: '0' }}>Team & Access</div>
            <span style={{ fontSize: '12px', color: 'var(--text-light)' }}>
              {teamMembers.length} members
            </span>
          </div>
          {teamMembers.map(u => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--border-light)' }}>
              <div style={{ 
                width: '36px', 
                height: '36px', 
                borderRadius: '50%', 
                background: u.color, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '14px', 
                fontWeight: '700', 
                color: 'white',
                cursor: 'pointer',
                flexShrink: 0
              }}
              onClick={() => handleViewTeamMember(u)}
              title="View Details"
              >
                {u.name.split(' ')[0][0]}{u.name.split(' ')[1]?.[0] || ''}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: '600' }}>{u.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{u.email}</div>
              </div>
              <span className={`badge ${getRoleBadgeClass(u.role)}`}>{u.role}</span>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button 
                  className="btn btn-ghost btn-sm btn-icon" 
                  onClick={() => handleViewTeamMember(u)}
                  title="View Details"
                >
                  <Eye size={16} />
                </button>
                <button
                  className="btn btn-ghost btn-sm btn-icon"
                  onClick={() => handleEditTeamMember(u)}
                  title="Edit"
                >
                  <Pencil size={16} />
                </button>
                <button
                  className="btn btn-ghost btn-sm btn-icon"
                  onClick={() => handleRemoveTeamMember(u)}
                  title="Remove"
                  style={{ color: 'var(--red)' }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
          <button className="btn btn-gold btn-sm" style={{ marginTop: '12px', width: '100%' }} onClick={handleInviteTeamMember}>
            <UserPlus size={16} style={{ verticalAlign: '-3px' }} /> Invite Team Member
          </button>
        </div>

        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header">
            <div className="card-title" style={{ marginBottom: '0' }}><Bell size={16} style={{ verticalAlign: '-3px' }} /> Notification Preferences</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button className="btn btn-ghost btn-sm" onClick={handleResetNotifications} disabled={isSubmitting || isLoadingPreferences}>
                <RefreshCw size={14} style={{ verticalAlign: '-2px' }} /> Reset
              </button>
              <button
                className="btn btn-gold btn-sm"
                onClick={handleSaveNotifications}
                disabled={isSubmitting || isLoadingPreferences}
              >
                {isSubmitting ? <><Loader2 size={14} style={{ verticalAlign: '-2px' }} /> Saving...</> : <><Save size={14} style={{ verticalAlign: '-2px' }} /> Save</>}
              </button>
            </div>
          </div>
          {isLoadingPreferences ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-light)' }}>
              <div style={{ marginBottom: '8px' }}><Loader2 size={20} /></div>
              <div>Loading preferences...</div>
            </div>
          ) : (
            notifications.map((n) => (
              <div key={n.id} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                padding: '10px 0',
                borderBottom: '1px solid var(--border-light)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ display: 'inline-flex' }}><n.icon size={20} /></span>
                  <div>
                    <div style={{ fontSize: '13px', color: 'var(--text)' }}>{n.label}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{n.desc}</div>
                  </div>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox" 
                    checked={n.on} 
                    onChange={() => toggleNotification(n.id)}
                    disabled={isSubmitting}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            ))
          )}
        </div>

        <div className="card">
          <div className="card-title" style={{ marginBottom: '16px', color: 'var(--red)' }}><AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> Danger Zone</div>
          <div style={{ border: '1px solid #fca5a5', borderRadius: '8px', padding: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--red)', marginBottom: '4px' }}>Delete All Member Data</div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '10px' }}>This will permanently remove all member records. This action cannot be undone.</div>
            <button className="btn btn-danger btn-sm" onClick={handleDeleteAllData}><Trash2 size={14} style={{ verticalAlign: '-2px' }} /> Delete All Data</button>
          </div>
        </div>
      </div>

      <style>{`
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
        }
        .badge-blue {
          background: var(--blue-bg);
          color: var(--blue);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .btn-danger {
          background: var(--red);
          color: white;
          border: none;
        }
        .btn-danger:hover {
          background: var(--red-dark);
        }
        .btn-gold:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .toggle {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 22px;
          flex-shrink: 0;
        }
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #ccc;
          transition: 0.3s;
          border-radius: 22px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle input:checked + .toggle-slider {
          background: var(--gold);
        }
        .toggle input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }
        @media (max-width: 768px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Settings;